import { z } from "zod";
import { idSchema } from "./common.ts";
import { themeTokensSchema } from "./editor.ts";

export const importJobStatusSchema = z.enum(["queued", "running", "awaiting_review", "completed", "failed", "cancelled"]);
export type ImportJobStatus = z.infer<typeof importJobStatusSchema>;

export const importedVariantSchema = z.object({ name: z.string().min(1).max(80), priceMinor: z.number().int().min(0) });

export const importedMenuItemSchema = z.object({
  name: z.string().min(1).max(160),
  description: z.string().max(2000).optional(),
  priceMinor: z.number().int().min(0).optional(),
  currency: z.string().length(3).optional(),
  category: z.string().min(1).max(120),
  variants: z.array(importedVariantSchema).max(20).optional(),
  /** Heuristic quality score, not a calibrated probability. */
  confidence: z.number().min(0).max(1).optional(),
  sourcePage: z.number().int().min(1).optional(),
  sourceRegion: z.object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() }).optional(),
  sourceUrl: z.string().max(2000).optional(),
  sourceText: z.string().max(1000).optional(),
  imageUrl: z.string().max(2000).optional(),
  /** Allergen text found in the source; never converted to structured allergen claims automatically. */
  allergenText: z.string().max(500).optional(),
  needsReview: z.boolean(),
  reviewReasons: z.array(z.string().max(120)).max(10).default([]),
});
export type ImportedMenuItem = z.infer<typeof importedMenuItemSchema>;

export const importedCategorySchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(1000).optional(),
});

export const importDesignSchema = z.object({
  tokens: themeTokensSchema.partial(),
  suggestedThemeId: z.string().max(64).optional(),
  layout: z.enum(["list", "grid", "cards"]).optional(),
  heroImageUrl: z.string().max(2000).optional(),
  logoUrl: z.string().max(2000).optional(),
  evidence: z.array(z.string().max(300)).max(30).default([]),
});
export type ImportDesign = z.infer<typeof importDesignSchema>;

export const importExtractionSchema = z.object({
  restaurantName: z.string().max(160).optional(),
  detectedLanguage: z.string().max(16).optional(),
  detectedCurrency: z.string().length(3).optional(),
  categories: z.array(importedCategorySchema).max(100),
  items: z.array(importedMenuItemSchema).max(1000),
  design: importDesignSchema.optional(),
  warnings: z.array(z.string().max(300)).max(50).default([]),
  pages: z
    .array(z.object({ index: z.number().int(), url: z.string().max(2000).optional(), screenshotAssetId: z.string().optional(), title: z.string().max(300).optional() }))
    .max(100)
    .default([]),
});
export type ImportExtraction = z.infer<typeof importExtractionSchema>;

export const startWebsiteImportSchema = z.object({
  restaurantId: idSchema,
  menuId: idSchema.optional(),
  url: z.string().url().max(2000),
  confirmRights: z.literal(true, { message: "You must confirm you own or have permission to use this content" }),
});

export const startPdfImportSchema = z.object({
  restaurantId: idSchema,
  menuId: idSchema.optional(),
  assetId: idSchema,
  confirmRights: z.literal(true, { message: "You must confirm you own or have permission to use this content" }),
});

export const applyImportSchema = z.object({
  /** Reviewed extraction (user may have edited it). */
  extraction: importExtractionSchema,
  mode: z.enum(["new_menu", "replace", "append"]).default("new_menu"),
  menuName: z.string().min(1).max(120).optional(),
  applyDesign: z.boolean().default(true),
});

export type ImportJobDto = {
  id: string;
  sourceType: "website" | "pdf";
  sourceUrl: string | null;
  sourceAssetId: string | null;
  sourceAssetUrl: string | null;
  restaurantId: string;
  menuId: string | null;
  status: ImportJobStatus;
  stage: string | null;
  progress: number;
  error: string | null;
  extraction: ImportExtraction | null;
  creditsCharged: number;
  createdAt: string;
  completedAt: string | null;
  resultMenuId: string | null;
};
